import { createElement, useMemo, type ReactNode } from 'react';
import {
  CalendarOutlined,
  FieldTimeOutlined,
  FlagOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  KANBAN_SORT_GROUPS,
  KANBAN_SORT_OPTIONS_BY_GROUP,
  type KanbanSortGroup,
  type KanbanSortOption,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

export type KanbanSortFilterOption = {
  value: KanbanSortOption;
  label: string;
  icon: ReactNode;
  color: string;
};

export type KanbanSortFilterGroup = {
  key: KanbanSortGroup;
  label: string;
  options: KanbanSortFilterOption[];
};

const SORT_GROUP_VISUAL: Record<
  KanbanSortGroup,
  { color: string; Icon: typeof FieldTimeOutlined }
> = {
  updated: {
    color: WORK_ORDER_UI.primary,
    Icon: FieldTimeOutlined,
  },
  created: {
    color: WORK_ORDER_UI.columnHeader.PLANNED.badge,
    Icon: PlusCircleOutlined,
  },
  priority: {
    color: WORK_ORDER_UI.priority.HIGH.fg,
    Icon: FlagOutlined,
  },
  due: {
    color: WORK_ORDER_UI.columnHeader.ASSIGNED.badge,
    Icon: CalendarOutlined,
  },
};

export function useWorkOrderKanbanSortOptions(): {
  groups: KanbanSortFilterGroup[];
  flatOptions: KanbanSortFilterOption[];
} {
  const { t } = useTranslation('maintenance');

  return useMemo(() => {
    const groups = KANBAN_SORT_GROUPS.map((group) => {
      const visual = SORT_GROUP_VISUAL[group];
      const icon = createElement(visual.Icon, {
        style: { color: visual.color },
      });
      return {
        key: group,
        label: t(`workOrders.kanban.filters.sortGroups.${group}`),
        options: KANBAN_SORT_OPTIONS_BY_GROUP[group].map((value) => ({
          value,
          label: t(`workOrders.kanban.filters.sort.${value}`),
          color: visual.color,
          icon,
        })),
      };
    });

    return {
      groups,
      flatOptions: groups.flatMap((group) => group.options),
    };
  }, [t]);
}
